<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Button from 'primevue/button';
import Drawer from 'primevue/drawer';
import UserAvatar from '@/components/UserAvatar.vue';
import { useAuth } from '@/composables/useAuth';

const route = useRoute();
const router = useRouter();
const { user, isAdmin, clearSession } = useAuth();

const menuOpen = ref(false);

const isCompact = computed(() => Boolean(route.meta.fillHeight));

const navItems = computed(() => [
  { label: 'Главная', icon: 'pi pi-home', to: '/app/home' },
  { label: 'Стена Плача', icon: 'pi pi-comments', to: '/app/wall' },
  { label: 'Пользователи', icon: 'pi pi-users', to: '/app/users' },
  { label: 'Подарки', icon: 'pi pi-gift', to: '/app/gifts' },
  { label: 'Игральня', icon: 'pi pi-bolt', to: '/app/playground' },
]);

watch(
  () => route.path,
  () => {
    menuOpen.value = false;
  },
);

function isActive(path: string) {
  return route.path === path || route.path.startsWith(`${path}/`);
}

function goSettings() {
  menuOpen.value = false;
  router.push({ name: 'settings' });
}

function goAdmin() {
  menuOpen.value = false;
  router.push({ name: 'admin' });
}

function logout() {
  menuOpen.value = false;
  clearSession();
  router.push({ name: 'landing' });
}
</script>

<template>
  <header class="app-header" :class="{ 'app-header--compact': isCompact }">
    <div class="app-header__brand">
      <span class="app-header__logo">🎀</span>
      <div class="app-header__brand-text">
        <strong class="app-header__title">Убежище</strong>
        <small class="app-header__tagline">hp · нас тут человек десять</small>
      </div>
    </div>

    <nav v-if="!isCompact" class="app-header__nav app-header__nav--desktop">
      <RouterLink
        v-for="item in navItems"
        :key="item.to"
        :to="item.to"
        class="app-header__link"
        :class="{ 'app-header__link--active': isActive(item.to) }"
      >
        <i :class="item.icon" />
        {{ item.label }}
      </RouterLink>
    </nav>

    <div class="app-header__actions app-header__actions--desktop">
      <Button
        v-if="isAdmin && !isCompact"
        label="Админ-панель"
        icon="pi pi-shield"
        severity="help"
        size="small"
        @click="goAdmin"
      />
      <Button
        v-if="isAdmin && isCompact"
        icon="pi pi-shield"
        severity="help"
        size="small"
        rounded
        aria-label="Админ-панель"
        @click="goAdmin"
      />
      <button type="button" class="app-header__profile" @click="goSettings">
        <UserAvatar v-if="user" :nickname="user.nickname" :avatar="user.avatar" size="normal" />
        <span class="app-header__profile-name">{{ user?.nickname }}</span>
      </button>
      <Button icon="pi pi-sign-out" severity="secondary" text rounded @click="logout" />
    </div>

    <Button
      class="app-header__burger"
      icon="pi pi-bars"
      severity="secondary"
      text
      rounded
      size="small"
      aria-label="Меню"
      @click="menuOpen = true"
    />

    <Drawer
      v-model:visible="menuOpen"
      position="right"
      header="Меню"
      class="app-header__drawer"
      :style="{ width: 'min(320px, 88vw)' }"
    >
      <div class="app-header__drawer-user">
        <UserAvatar v-if="user" :nickname="user.nickname" :avatar="user.avatar" size="large" />
        <div>
          <strong>{{ user?.nickname }}</strong>
          <small>Убежище · hp</small>
        </div>
      </div>

      <nav class="app-header__drawer-nav">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="app-header__drawer-link"
          :class="{ 'app-header__drawer-link--active': isActive(item.to) }"
        >
          <i :class="item.icon" />
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="app-header__drawer-footer">
        <Button
          v-if="isAdmin"
          label="Админ-панель"
          icon="pi pi-shield"
          severity="help"
          fluid
          @click="goAdmin"
        />
        <Button label="Настройки" icon="pi pi-cog" severity="secondary" outlined fluid @click="goSettings" />
        <Button label="Выйти" icon="pi pi-sign-out" severity="danger" text fluid @click="logout" />
      </div>
    </Drawer>
  </header>
</template>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  padding-top: calc(0.85rem + env(safe-area-inset-top, 0px));
  background: rgba(255, 255, 255, 0.92);
  border-bottom: 1px solid #fce7f3;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 20;
  flex-shrink: 0;
}

.app-header--compact {
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  padding-top: calc(0.45rem + env(safe-area-inset-top, 0px));
}

.app-header--compact .app-header__brand {
  min-width: 0;
  flex: 1;
}

.app-header--compact .app-header__tagline {
  display: none;
}

.app-header__brand {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;
}

.app-header__brand-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.app-header__title {
  color: #831843;
  font-size: 1rem;
  line-height: 1.15;
  white-space: nowrap;
}

.app-header__tagline {
  color: #be185d;
  font-size: 0.8125rem;
  line-height: 1.15;
  white-space: nowrap;
}

.app-header__logo {
  font-size: 1.35rem;
  line-height: 1;
  flex-shrink: 0;
}

.app-header__nav {
  display: flex;
  flex: 1;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.app-header__link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  color: #9d174d;
  text-decoration: none;
  font-weight: 600;
}

.app-header__link--active,
.app-header__link:hover {
  background: #fce7f3;
}

.app-header__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.app-header__profile {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #831843;
  font-weight: 600;
}

.app-header__burger {
  display: none;
  margin-left: auto;
  flex-shrink: 0;
}

.app-header__drawer-user {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.25rem 0 1rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #fce7f3;
}

.app-header__drawer-user strong {
  display: block;
  color: #831843;
}

.app-header__drawer-user small {
  color: #be185d;
}

.app-header__drawer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.app-header__drawer-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-radius: 1rem;
  color: #831843;
  text-decoration: none;
  font-weight: 600;
}

.app-header__drawer-link--active,
.app-header__drawer-link:hover {
  background: #fdf2f8;
}

.app-header__drawer-footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #fce7f3;
}

@media (max-width: 900px) {
  .app-header {
    gap: 0.5rem;
    padding: 0.4rem 0.75rem;
    padding-top: calc(0.4rem + env(safe-area-inset-top, 0px));
  }

  .app-header--compact {
    padding: 0.35rem 0.65rem;
    padding-top: calc(0.35rem + env(safe-area-inset-top, 0px));
  }

  .app-header__nav--desktop,
  .app-header__actions--desktop {
    display: none;
  }

  .app-header__burger {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    padding: 0;
  }

  .app-header__brand {
    gap: 0.4rem;
    align-items: center;
  }

  .app-header__brand-text {
    gap: 0;
  }

  .app-header__logo {
    font-size: 1.15rem;
  }

  .app-header__title {
    font-size: 0.9375rem;
    line-height: 1;
  }

  .app-header__tagline {
    display: none;
  }
}

@media (min-width: 901px) {
  .app-header__brand {
    min-width: 180px;
    flex: 0 1 auto;
    gap: 0.75rem;
  }

  .app-header__title {
    font-size: 1.05rem;
  }

  .app-header__logo {
    font-size: 1.6rem;
  }
}
</style>
